import { ReflectionType } from "../../../../../types/domain-types";
import { Category } from "../../types";
import { DynamicTestSetWithCategory } from "../../utils";

/**
 * Counts dynamic test sets by category and reflection type
 */
export function countByCategory(
  dynamicTestSets: DynamicTestSetWithCategory[]
): Record<Category, Record<ReflectionType, number>> {
  return dynamicTestSets.reduce((acc, dts) => {
    const category = dts.category as Category;
    const type = dts.type as ReflectionType;

    if (!category || !type) return acc;

    if (!acc[category]) {
      acc[category] = {
        [ReflectionType.Leverage]: 0,
        [ReflectionType.Challenge]: 0,
      };
    }

    acc[category][type]++;

    return acc;
  }, {} as Record<Category, Record<ReflectionType, number>>);
}

/**
 * Calculates total counts per reflection type across all categories
 */
export function calculateTotalsByType(
  countsByCategory: Record<Category, Record<ReflectionType, number>>
): Record<ReflectionType, number> {
  return Object.values(countsByCategory).reduce(
    (acc, counts) => {
      acc[ReflectionType.Leverage] += counts[ReflectionType.Leverage] || 0;
      acc[ReflectionType.Challenge] += counts[ReflectionType.Challenge] || 0;
      return acc;
    },
    { [ReflectionType.Leverage]: 0, [ReflectionType.Challenge]: 0 }
  );
}

/**
 * Converts counts to percentages where each reflection type sums to 100% across all categories
 */
export function convertCountsToPercentages(
  countsByCategory: Record<Category, Record<ReflectionType, number>>,
  totalsByType: Record<ReflectionType, number>
): Partial<Record<Category, Record<ReflectionType, number>>> {
  return Object.entries(countsByCategory).reduce((acc, [category, values]) => {
    acc[category as Category] = {
      [ReflectionType.Leverage]:
        totalsByType[ReflectionType.Leverage] > 0
          ? Math.round(
              (values[ReflectionType.Leverage] /
                totalsByType[ReflectionType.Leverage]) *
                100
            )
          : 0,
      [ReflectionType.Challenge]:
        totalsByType[ReflectionType.Challenge] > 0
          ? Math.round(
              (values[ReflectionType.Challenge] /
                totalsByType[ReflectionType.Challenge]) *
                100
            )
          : 0,
    };
    return acc;
  }, {} as Partial<Record<Category, Record<ReflectionType, number>>>);
}
